
import Vue from 'vue';
import Loader from '@customer/components/base/Loader.vue';
import BaseIcon from '@customer/components/base/BaseIcon.vue';
import { oneOf } from '@customer/utils/helpers';

interface Props {
  theme: string;
  size: string;
  icon: string;
  iconPosition: string;
  loading: boolean;
}

interface Data {
  showSlot: boolean;
}

interface Computed {
  iconLeft: boolean;
  iconRight: boolean;
  classes: (string | { [x: string]: boolean })[];
  tagProps: Record<string, unknown>;
}

export default Vue.extend<Data, unknown, Computed, Props>({
  components: {
    Loader,
    BaseIcon,
  },
  inheritAttrs: false,
  props: {
    // Base styles for button
    theme: {
      validator(value: string) {
        return oneOf(value, ['primary', 'secondary', '']);
      },
      type: String,
      // No styles by default
      // Use util classes to change button appearance
      default: '',
      required: false,
    },
    size: {
      validator(value: string) {
        return oneOf(value, ['default', 'auto']);
      },
      type: String,
      default: 'default',
      required: false,
    },
    // Takes icon from svg/icons folder
    icon: {
      type: String,
      default: '',
      required: false,
    },
    iconPosition: {
      validator(value: string) {
        return oneOf(value, ['left', 'right']);
      },
      type: String,
      default: 'right',
      required: false,
    },
    loading: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data() {
    return {
      showSlot: true,
    };
  },
  computed: {
    iconLeft() {
      return !!this.icon && this.iconPosition === 'left';
    },
    iconRight() {
      return !!this.icon && this.iconPosition === 'right';
    },
    classes() {
      const iconOnly: boolean = !this.showSlot && !!this.icon;
      return [
        'S-button',
        {
          [`S-button--${this.theme}`]: !!this.theme,
          [`S-button--size-${this.size}`]: !iconOnly,
        },
      ];
    },
    // Creates props for LinkHandler
    tagProps() {
      const { $attrs, classes } = this;

      if (!$attrs.type) {
        $attrs.type = 'button';
      }
      // Return new object with all inherited props.
      return { ...$attrs, class: classes };
    },
  },
  created() {
    this.showSlot = this.$slots.default !== undefined;
  },
});
